import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Snackbar, Alert } from "@mui/material";
import { useTranslation } from 'react-i18next';
import { useDirectionContext } from '../../contexts/DirectionContext';

/**
 * Refined SignIn component with local development bypass
 */
export default function SignInRedesign(props) {
  const { t } = useTranslation();
  const direction = useDirectionContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [resendIn, setResendIn] = useState(0);

  const otpLen = 6;

  // Development bypass configuration
  const isDevelopment = process.env.NODE_ENV === 'development';
  const shouldBypass = isDevelopment && process.env.REACT_APP_BYPASS_AUTH === 'true';
  const BYPASS_PHONE = '0548004990';

  const cleanDigits = (v) => (v || "").replace(/\D/g, "");

  const isILMobile = useMemo(() => {
    const d = cleanDigits(props.ph);
    const localValid = /^05\d{8}$/.test(d);
    const intlValid = /^9725\d{8}$/.test(d);
    return localValid || intlValid;
  }, [props.ph]);

  const normalizedE164 = useMemo(() => {
    const d = cleanDigits(props.ph);
    if (/^9725\d{8}$/.test(d)) return `+${d}`;
    if (/^05\d{8}$/.test(d)) return `+972${d.slice(1)}`;
    return "";
  }, [props.ph]);

  // Auto-bypass in development mode
  useEffect(() => {
    if (shouldBypass && !props.showOTP) {
      console.log("🔧 DEV MODE: Auto-bypassing authentication");
      props.setPh?.(BYPASS_PHONE);
      // Small delay to make it feel natural
      setTimeout(() => {
        handleDevBypass();
      }, 100);
    }
  }, [shouldBypass, props.showOTP, props.setPh]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  // Development bypass function
  const handleDevBypass = () => {
    props.onClick?.();
    props.setShowOTP?.(true);
    props.setManager?.({
      id: 'dev-manager-123',
      name: 'Development User',
      phone: BYPASS_PHONE,
      organization: 'Dev Organization'
    });
    setSuccess("🔧 DEV MODE: קוד אימות נשלח (bypass מופעל)");
    setResendIn(30); // Shorter time for dev

    // Auto-fill OTP after a moment
    setTimeout(() => {
      props.setOtp?.('123456');
    }, 500);
  };

  async function handleSendCode() {
    // Development bypass
    if (shouldBypass) {
      console.log("🔧 DEV MODE: Bypassing SMS send");
      handleDevBypass();
      return;
    }

    // Production logic
    if (!isILMobile) {
      setError(t('auth.phoneInvalidError'));
      return;
    }

    try {
      setLoading(true);
      setError("");
      const serverPhone = normalizedE164.replace("+", "%2B");
      const url = `${window.AppConfig.serverDomain}/api/organzations/get-orgs/${serverPhone}`;
      const response = await axios.get(url);

      if (response?.data?.result?.length > 0) {
        props.onClick?.();
        props.setShowOTP?.(true);
        props.setManager?.(response.data.manager);
        setSuccess(t('auth.codeSentSuccess'));
        setResendIn(60);
      } else {
        setError(t('auth.phoneNotInSystem'));
      }
    } catch (e) {
      console.error(e);
      setError(t('auth.sendError'));
    } finally {
      setLoading(false);
    }
  }

  function handleVerify() {
    // Development bypass - accept any OTP
    if (shouldBypass) {
      console.log("🔧 DEV MODE: Bypassing OTP verification");
      setError("");
      setSuccess("🔧 DEV MODE: התחברות הושלמה בהצלחה!");
      setTimeout(() => {
        if (typeof props.onOTPVerify === 'function') {
          props.onOTPVerify();
        } else {
          console.log("🔧 DEV MODE: onOTPVerify function not provided, login complete");
          // You can add additional logic here if needed
        }
      }, 300);
      return;
    }

    // Production logic
    if ((props.otp || "").length !== otpLen) {
      setError(t('auth.enterFullCodeError'));
      return;
    }
    setError("");
    if (typeof props.onOTPVerify === 'function') {
      props.onOTPVerify();
    } else {
      console.error("onOTPVerify function not provided in props");
      setError(t('auth.configError'));
    }
  }

  function handleResend() {
    if (resendIn > 0) return;
    handleSendCode();
  }

  const inputStyle = (hasError) => ({
    width: '100%',
    padding: '11px 14px',
    fontSize: '15px',
    border: `1px solid ${hasError ? '#ef4444' : '#d1d5db'}`,
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box',
    color: '#111',
    background: '#fff',
    transition: 'border-color 0.12s',
    fontFamily: 'inherit',
  });

  const btnStyle = (disabled) => ({
    width: '100%',
    padding: '12px',
    background: disabled ? '#94a3b8' : '#1324a1',
    color: '#fff',
    fontSize: '15px',
    fontWeight: 600,
    border: 'none',
    borderRadius: '8px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'background 0.12s',
    fontFamily: 'inherit',
  });

  const align = direction === 'rtl' ? 'right' : 'left';

  return (
    <div dir={direction} style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#fafafa',
      padding: '24px 24px 80px',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', sans-serif",
    }}>

      {/* Toast notifications */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError("")} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={() => setError("")} severity="error" variant="filled" sx={{ width: "100%" }}>{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess("")} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={() => setSuccess("")} severity="success" variant="filled" sx={{ width: "100%" }}>{success}</Alert>
      </Snackbar>

      {/* Dev mode banner */}
      {isDevelopment && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, background: '#f59e0b', color: '#fff', textAlign: 'center', padding: '6px', fontSize: '12px', fontWeight: 700, zIndex: 9999 }}>
          🔧 DEVELOPMENT MODE {shouldBypass && '— AUTO BYPASS ENABLED'}
        </div>
      )}

      {/* Form — no card, no shadow, form floats on background */}
      <div style={{
        width: '100%',
        maxWidth: '360px',
        marginTop: isDevelopment ? '32px' : '0',
      }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <img src="/logo.png" alt="Varno" style={{ height: '56px', width: 'auto' }} />
        </div>

        {!props.showOTP ? (
          <>
            <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#111', margin: '0 0 6px', letterSpacing: '-0.02em', lineHeight: 1.2, textAlign: align }}>
              {t('auth.signIn')}
            </h1>
            <p style={{ fontSize: '14px', color: '#666', margin: '0 0 32px', lineHeight: 1.6, textAlign: align }}>
              {t('auth.enterPhoneInstructions')}
            </p>

            <form onSubmit={(e) => { e.preventDefault(); handleSendCode(); }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#444', marginBottom: '6px', textAlign: align }}>
                  {t('auth.phoneLabel')}
                </label>
                <input
                  type="tel"
                  value={props.ph}
                  onChange={(e) => props.setPh?.(e.target.value)}
                  placeholder="05X-XXX-XXXX"
                  autoFocus={!shouldBypass}
                  inputMode="tel"
                  dir="ltr"
                  style={{ ...inputStyle(!!props.ph && !isILMobile), textAlign: align }}
                  onFocus={(e) => { e.target.style.borderColor = '#1324a1'; }}
                  onBlur={(e) => { e.target.style.borderColor = props.ph && !isILMobile ? '#ef4444' : '#d1d5db'; }}
                  aria-required="true"
                />
                {props.ph && !isILMobile && (
                  <p style={{ fontSize: '12px', margin: '5px 0 0', color: '#ef4444', textAlign: align }}>
                    {t('auth.phoneInvalidError')}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                style={btnStyle(loading)}
                onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = '#0f1d7a'; }}
                onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = '#1324a1'; }}
              >
                {loading ? t('auth.sending') : t('auth.sendCode')}
              </button>
            </form>

            {isDevelopment && (
              <div style={{ marginTop: '24px' }}>
                <button
                  onClick={() => { props.setPh?.(BYPASS_PHONE); handleDevBypass(); }}
                  style={{ width: '100%', padding: '10px', background: 'transparent', border: '1px dashed #f59e0b', color: '#d97706', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontFamily: 'inherit' }}
                >
                  🔧 DEV: Bypass Login ({BYPASS_PHONE})
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#111', margin: '0 0 6px', letterSpacing: '-0.02em', lineHeight: 1.2, textAlign: align }}>
              {t('auth.enterCodeInstructions')}
            </h1>
            <p style={{ fontSize: '14px', color: '#666', margin: '0 0 32px', lineHeight: 1.6, textAlign: align }}>
              {shouldBypass
                ? <span style={{ color: '#d97706' }}>DEV MODE — כל קוד יתקבל</span>
                : t('auth.noCodeHelp')
              }
            </p>

            <div style={{ marginBottom: '16px' }}>
              <input
                type="text"
                value={props.otp || ""}
                onChange={(e) => props.setOtp?.(cleanDigits(e.target.value).slice(0, otpLen))}
                autoFocus
                placeholder="• • • • • •"
                inputMode="numeric"
                dir="ltr"
                maxLength={otpLen}
                style={{
                  ...inputStyle(false),
                  fontSize: '26px',
                  fontWeight: 600,
                  letterSpacing: '12px',
                  textAlign: 'center',
                  padding: '16px',
                }}
                onFocus={(e) => { e.target.style.borderColor = '#1324a1'; }}
                onBlur={(e) => { e.target.style.borderColor = '#d1d5db'; }}
                aria-label={t('auth.otpGroupAriaLabel')}
                aria-required="true"
              />
            </div>

            <button
              onClick={handleVerify}
              disabled={loading}
              style={{ ...btnStyle(loading), marginBottom: '16px' }}
              onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = '#0f1d7a'; }}
              onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = '#1324a1'; }}
            >
              {t('auth.verify')}
            </button>

            <div style={{ textAlign: 'center' }}>
              <button
                onClick={handleResend}
                disabled={resendIn > 0 || loading}
                style={{ background: 'none', border: 'none', fontSize: '13px', cursor: resendIn > 0 ? 'default' : 'pointer', color: resendIn > 0 ? '#aaa' : '#1324a1', fontWeight: 500, padding: 0, fontFamily: 'inherit' }}
              >
                {resendIn > 0 ? t('auth.resendTimer', { seconds: resendIn }) : t('auth.resend')}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Support footer */}
      <div style={{ position: 'fixed', bottom: '24px', width: '100%', textAlign: 'center', fontSize: '13px', color: '#aaa' }}>
        {t('auth.supportText')}{' '}
        <a href="mailto:support@varno.io" style={{ color: '#1324a1', textDecoration: 'none', fontWeight: 500 }}>support@varno.io</a>
      </div>
    </div>
  );
}